import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../../api";
import { n2, round2 } from "../../lib/format";
import "./pos.css";

/*
 * Warehouse POS – port of warehousePos.php (API: backend/warehouse.py)
 * Sells inventory_items through CALL stocksale(). Same flow as PHP: pick a client and payment
 * method, add items, enter the amount received (must cover the subtotal), complete the sale.
 */

const PAYMENT_METHODS = [["Cash", "Cash"], ["LipaNumber", "LipaNumber"], ["Bank", "Bank"]];

export default function WarehousePos() {
  const api = useApi();
  const [boot, setBoot] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [clientId, setClientId] = useState(0);
  const [method, setMethod] = useState("Cash");
  const [received, setReceived] = useState("");
  const [notice, setNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async (keepClient) => {
    try {
      const d = await api.get("/api/warehouse/pos");
      setBoot(d);
      if (!keepClient) setClientId(d.clients[0]?.id || 0);
      setLoadError(null);
    } catch (e) { setLoadError(e.message); }
  }, [api]);
  useEffect(() => { load(false); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = boot?.items || [];
    return q ? list.filter((i) => i.name.toLowerCase().includes(q) || (i.category || "").toLowerCase().includes(q)) : list;
  }, [boot, search]);
  const subtotal = round2(cart.reduce((s, i) => s + i.price * i.qty, 0));
  const receivedNum = parseFloat(received) || 0;

  const add = (item) => setCart((c) => (c.some((i) => i.id === item.id)
    ? c.map((i) => (i.id === item.id ? { ...i, qty: i.qty + 1 } : i))
    : [...c, { ...item, qty: 1 }]));
  const inc = (idx) => setCart((c) => c.map((i, k) => (k === idx ? { ...i, qty: i.qty + 1 } : i)));
  const dec = (idx) => setCart((c) => (c[idx].qty > 1 ? c.map((i, k) => (k === idx ? { ...i, qty: i.qty - 1 } : i)) : c.filter((_, k) => k !== idx)));

  const complete = async () => {
    if (!cart.length) { setNotice({ tone: "err", text: "Cart is empty." }); return; }
    if (receivedNum < subtotal) { setNotice({ tone: "err", text: "Received amount is less than total." }); return; }
    setSubmitting(true); setNotice(null);
    try {
      const r = await api.post("/api/warehouse/sale", {
        client_id: clientId, payment_method: method, received_amount: receivedNum,
        items: cart.map((i) => ({ id: i.id, qty: i.qty })),
      });
      setNotice({ tone: "ok", text: `Sale completed. Change: ${n2(r.change)} TZS` });
      setCart([]); setReceived("");
      load(true);
    } catch (e) {
      setNotice({ tone: "err", text: e.message });
    } finally { setSubmitting(false); }
  };

  return (
    <main className="pos-page">
      <div className="pos-head"><div><h1>Warehouse POS</h1><p>Sell warehouse inventory items</p></div></div>

      {notice && <div className={`pos-notice ${notice.tone}`} role={notice.tone === "err" ? "alert" : "status"}><span>{notice.text}</span>
        <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)}><i className="fas fa-xmark" /></button></div>}
      {loadError && <div className="pos-notice err" role="alert"><span>Couldn't load items: {loadError}</span><button type="button" className="btn" onClick={() => load(false)}>Retry</button></div>}

      <div className="pos-grid">
        <section className="panel pos-products" aria-label="Products">
          <div className="pos-search">
            <div className="box">
              <i className="fas fa-magnifying-glass" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or category" aria-label="Search items" autoFocus />
              {search && <button type="button" className="clear" aria-label="Clear search" onClick={() => setSearch("")}><i className="fas fa-xmark" /></button>}
            </div>
          </div>
          <div className="pos-cards">
            {!boot && !loadError && <div className="pos-empty"><i className="fas fa-spinner fa-spin" />Loading items…</div>}
            {filtered.map((i) => (
              <button key={i.id} type="button" className="pcard" onClick={() => add(i)}>
                <span className="nm">{i.name}</span>
                <span className="mf">{i.category || "No category"}</span>
                <span className="row"><span className="pr mono">{n2(i.price)}</span><span className="st ok">Stock: {i.stock} {i.unit}</span></span>
              </button>
            ))}
            {boot && filtered.length === 0 && <div className="pos-empty"><i className="fas fa-magnifying-glass" />No items match.</div>}
          </div>
        </section>

        <section className="panel pos-cart" aria-label="Cart">
          <div className="hd"><h2>Cart</h2><span className="pill">{cart.length} {cart.length === 1 ? "item" : "items"}</span></div>
          <div className="pos-opts">
            <div><label htmlFor="wh-client">Client</label>
              <select id="wh-client" value={clientId} onChange={(e) => setClientId(Number(e.target.value))}>
                {(boot?.clients || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div><label id="wh-method">Payment method</label>
              <div className="seg" role="group" aria-labelledby="wh-method">
                {PAYMENT_METHODS.map(([v, l]) => <button key={v} type="button" aria-pressed={method === v} onClick={() => setMethod(v)}>{l}</button>)}
              </div></div>
          </div>
          <div className="pos-lines">
            {cart.length === 0 ? <div className="cart-empty"><i className="fas fa-cart-shopping" />No items in cart.</div>
              : cart.map((item, idx) => (
                <div key={item.id} className="pos-line">
                  <div className="top">
                    <div style={{ minWidth: 0 }}><div className="nm">{item.name}</div><div className="sub mono">{n2(item.price)} TZS</div></div>
                    <div><div className="lt mono">{n2(item.qty * item.price)}</div>
                      <button type="button" className="rm" onClick={() => setCart((c) => c.filter((_, k) => k !== idx))}>Remove</button></div>
                  </div>
                  <div className="ctl"><span className="qty">
                    <button type="button" aria-label={`One less ${item.name}`} onClick={() => dec(idx)}>−</button>
                    <span className="mono">{item.qty}</span>
                    <button type="button" aria-label={`One more ${item.name}`} onClick={() => inc(idx)}>+</button></span></div>
                </div>
              ))}
          </div>
          <div className="pos-pay">
            <div className="sums"><div className="grand"><span>Subtotal</span><span className="mono">{n2(subtotal)} TZS</span></div></div>
            <div className="recv"><label htmlFor="wh-recv">Received amount</label>
              <input id="wh-recv" type="number" step="0.01" min="0" inputMode="decimal" placeholder="0.00" value={received} onChange={(e) => setReceived(e.target.value)} /></div>
            {cart.length > 0 && (receivedNum >= subtotal
              ? <div className="chg ok">Change: <strong className="mono">{n2(receivedNum - subtotal)} TZS</strong></div>
              : <div className="chg short">Need <strong className="mono">{n2(subtotal - receivedNum)}</strong> more</div>)}
            <div className="pos-actions">
              <button type="button" className="btn-pay" disabled={submitting || !cart.length || receivedNum < subtotal} onClick={complete}>
                {submitting ? "Completing…" : "Complete sale"}
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
